/** The three calls a command dialog makes: submit, inspect and step-up (UI spec §4). */
import {
  OUT_OF_BAND_PATH,
  STEP_UP_PATH,
  decodeAuthorisation,
  decodeIssuedCode,
  type ActionAuthorisation,
  type IssuedCode,
} from "../contracts/account.ts";
import { COMMANDS_PATH } from "../contracts/constants.ts";
import {
  decodeAdmitted,
  decodeCommandOutcome,
  encodeCommandRequest,
  type Admitted,
  type CommandOutcome,
  type CommandRequest,
} from "../contracts/commands.ts";
import type { ApiClient } from "./client.ts";
import type { IntentKeys } from "./intent-keys.ts";

/** What a step-up authorisation is bound to (the server binds all of it). */
export interface StepUpBinding {
  readonly action: string;
  readonly accountId: string;
  readonly environment: string;
  readonly payload: CommandRequest["payload"];
  readonly expectedVersion: number;
}

/** The single-use proof a step-up command presents; held in memory only (S12). */
export interface Proof {
  readonly authorisationId: string;
  readonly nonce: string;
}

/** An out-of-band confirmation code, typed by hand: held in memory, sent once, never stored (U049). */
export interface OutOfBand {
  readonly code: string;
}

export interface CommandPort {
  /**
   * `POST /api/v1/commands`; `null` when the view generation moved on. The out-of-band code rides
   * beside the request (never in the stored body, which a retry re-sends).
   */
  submit(
    request: CommandRequest,
    proof: Proof | null,
    confirmation?: OutOfBand | null,
  ): Promise<Admitted | null>;
  /** `GET /api/v1/commands/{id}`: the outcome, the way to inspect an unknown one after a refresh. */
  outcome(commandId: string): Promise<CommandOutcome | null>;
  /** `POST /api/v1/step-up`: a single-use authorisation after a fresh second factor. */
  stepUp(binding: StepUpBinding): Promise<ActionAuthorisation | null>;
  /**
   * `POST /api/v1/commands/out-of-band`: ask for a code through the caller's own channel. The
   * answer holds when it was issued, when it expires and the redacted channel, never the code.
   */
  requestCode(action: string, accountId: string): Promise<IssuedCode | null>;
}

/** Everything a view needs to issue commands: the port, the identities and the polling cadence. */
export interface CommandServices {
  readonly port: CommandPort;
  readonly keys: IntentKeys;
  /** Milliseconds between two outcome queries while a command is moving. */
  readonly pollMs: number;
}

/** The port over the API client (CSRF, claim-header refusal and generation handling are the client's). */
export function createCommandPort(client: Pick<ApiClient, "get" | "post">): CommandPort {
  return {
    submit: (request, proof, confirmation = null) =>
      client.post(
        COMMANDS_PATH,
        {
          ...encodeCommandRequest(request),
          ...(proof === null
            ? {}
            : { authorisation: { authorisation_id: proof.authorisationId, nonce: proof.nonce } }),
          ...(confirmation === null ? {} : { out_of_band: { code: confirmation.code } }),
        },
        decodeAdmitted,
      ),
    outcome: (commandId) =>
      client.get(
        `${COMMANDS_PATH}/${encodeURIComponent(commandId)}`,
        new URLSearchParams(),
        decodeCommandOutcome,
      ),
    requestCode: (action, accountId) =>
      client.post(OUT_OF_BAND_PATH, { action, account_id: accountId }, decodeIssuedCode),
    stepUp: (binding) =>
      client.post(
        STEP_UP_PATH,
        {
          action: binding.action,
          account_id: binding.accountId,
          environment: binding.environment,
          payload: binding.payload,
          expected_version: binding.expectedVersion,
        },
        decodeAuthorisation,
      ),
  };
}
